import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Box, HStack, Text, VStack } from '@bonusly/design-system';
import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { calculateTimeLeft } from '../../utils/date';
import { useGetActiveFlashPointsProgram } from './hooks/use-flash-points';

const baseColorsWithWhiteText = ['#1358B9', '#D82C2F', '#077F8D'];

export const FlashPointsCountdown: FC = () => {
	const { t } = useTranslation(['components', 'common']);
	const { flash_points_balance, time_zone } = useCurrentUser();
	const canAccessFlashPoints = usePermission(Permission.CanAccessFlashPoints);
	const { data: activeProgram } = useGetActiveFlashPointsProgram(flash_points_balance);

	const [, rerender] = useState(0);
	useEffect(() => {
		const interval = setInterval(() => {
			rerender((prev) => prev + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, []);

	if (!canAccessFlashPoints || !flash_points_balance || !activeProgram) return null;

	const timeLeft = calculateTimeLeft({ endDate: activeProgram.endDate, timezone: time_zone });
	const textColor = baseColorsWithWhiteText.includes(activeProgram.baseColor) ? 'white' : 'text.default';

	return (
		<Box
			p={5}
			mx={-1}
			mt={-1}
			borderTopRadius="md"
			bg={`linear-gradient(135deg, ${activeProgram.baseColor}, ${activeProgram.accentColor})`}
			data-testid="flash-points-countdown"
		>
			<VStack spacing={3}>
				<HStack justifyContent="space-between" w="100%">
					<Badge
						alignItems="center"
						borderRadius="xl"
						px={2.5}
						py={0.5}
						background="linear-gradient(105deg, #E6AE14 35%, 35%, #FCC84F 45%, 45%, #E6AE14 50%, 50%, #FCC84F 70%, 70%, #E6AE14 80%) no-repeat"
					>
						<Text color="yellow.700" fontWeight="semibold" fontSize={200}>
							{t('common:intl_number', { value: flash_points_balance })}
						</Text>
					</Badge>
					<Box>
						{Number(timeLeft.days) !== 0 && (
							<VStack display="inline-block" minW={10}>
								<Text color={textColor} fontSize={100} fontWeight="semibold">
									{t('common:pad_zero', { number: timeLeft.days })}
									<Text as="span" m={2}>
										:
									</Text>
								</Text>
								<Text color={textColor} fontSize={1}>
									{t('components:rich_media.amount.day', { count: Number(timeLeft.days) })}
								</Text>
							</VStack>
						)}
						<VStack display="inline-block" minW={10}>
							<Text color={textColor} fontSize={100} fontWeight="semibold">
								{t('common:pad_zero', { number: timeLeft.hours })}
								<Text as="span" m={2}>
									:
								</Text>
							</Text>
							<Text color={textColor} fontSize={1}>
								{t('components:rich_media.amount.hr', { count: Number(timeLeft.hours) })}
							</Text>
						</VStack>
						<VStack display="inline-block" minW={10}>
							<Text color={textColor} fontSize={100} fontWeight="semibold">
								{t('common:pad_zero', { number: timeLeft.minutes })}
								<Text as="span" m={2}>
									:
								</Text>
							</Text>
							<Text color={textColor} fontSize={1}>
								{t('components:rich_media.amount.min', { count: Number(timeLeft.minutes) })}
							</Text>
						</VStack>
						<VStack display="inline-block" minW={10}>
							<Text color={textColor} fontSize={100} fontWeight="semibold">
								{t('common:pad_zero', { number: timeLeft.seconds })}
							</Text>
							<Text color={textColor} fontSize={1}>
								{t('components:rich_media.amount.sec', { count: Number(timeLeft.seconds) })}
							</Text>
						</VStack>
					</Box>
				</HStack>
				<Text color={textColor} fontSize={50} fontWeight="semibold">
					{t('components:rich_media.amount.amount_popover_message')}
				</Text>
			</VStack>
		</Box>
	);
};
